<template>
  <div class="status-flow-config-management">
    <div class="page-header">
      <h2>状态流转配置管理</h2>
      <div class="header-actions">
        <el-button type="primary" @click="showCreateDialog" icon="el-icon-plus">
          新增配置
        </el-button>
        <el-button @click="initializeDefaults" icon="el-icon-setting">
          初始化默认配置
        </el-button>
        <el-button @click="validateRules" icon="el-icon-check">
          验证规则
        </el-button>
        <el-button @click="executeAutoTransition" icon="el-icon-refresh">
          执行自动流转
        </el-button>
      </div>
    </div>

    <!-- 搜索和过滤 -->
    <div class="search-section">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-select v-model="searchParams.fromStatus" placeholder="源状态" clearable>
            <el-option
              v-for="status in allStatuses"
              :key="status"
              :label="getStatusLabel(status)"
              :value="status">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="6">
          <el-select v-model="searchParams.toStatus" placeholder="目标状态" clearable>
            <el-option
              v-for="status in allStatuses"
              :key="status"
              :label="getStatusLabel(status)"
              :value="status">
            </el-option>
          </el-select>
        </el-col>
        <el-col :span="6">
          <el-select v-model="searchParams.activeOnly" placeholder="状态">
            <el-option label="全部" :value="null"></el-option>
            <el-option label="启用" :value="true"></el-option>
            <el-option label="禁用" :value="false"></el-option>
          </el-select>
        </el-col>
        <el-col :span="6">
          <el-button type="primary" @click="loadConfigs" icon="el-icon-search">
            搜索
          </el-button>
          <el-button @click="resetSearch" icon="el-icon-refresh">
            重置
          </el-button>
        </el-col>
      </el-row>
    </div>

    <!-- 配置列表 -->
    <div class="config-table">
      <el-table
        :data="configs"
        v-loading="loading"
        stripe
        border
        @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="fromStatus" label="源状态" width="120">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.fromStatus)">
              {{ getStatusLabel(scope.row.fromStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="toStatus" label="目标状态" width="120">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.toStatus)">
              {{ getStatusLabel(scope.row.toStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="allowedRoles" label="允许角色" width="200">
          <template slot-scope="scope">
            <el-tag
              v-for="role in parseRoles(scope.row.allowedRoles)"
              :key="role"
              size="mini"
              style="margin-right: 5px;">
              {{ getRoleLabel(role) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="priority" label="优先级" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.priority <= 1 ? 'success' : scope.row.priority <= 2 ? 'warning' : 'info'">
              {{ scope.row.priority }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="isActive" label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.isActive"
              @change="toggleActiveStatus(scope.row)">
            </el-switch>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="描述" min-width="150"></el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="160">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" @click="editConfig(scope.row)" icon="el-icon-edit">
              编辑
            </el-button>
            <el-button size="mini" @click="testTransition(scope.row)" icon="el-icon-view">
              测试
            </el-button>
            <el-button
              size="mini"
              type="danger"
              @click="deleteConfig(scope.row)"
              icon="el-icon-delete">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 批量操作 -->
    <div class="batch-actions" v-if="selectedConfigs.length > 0">
      <el-alert
        :title="`已选择 ${selectedConfigs.length} 项配置`"
        type="info"
        show-icon
        :closable="false">
      </el-alert>
      <div style="margin-top: 10px;">
        <el-button type="danger" @click="batchDelete" icon="el-icon-delete">
          批量删除
        </el-button>
      </div>
    </div>

    <!-- 创建/编辑对话框 -->
    <el-dialog
      :title="dialogMode === 'create' ? '新增配置' : '编辑配置'"
      :visible.sync="dialogVisible"
      width="600px"
      @close="resetForm">
      <el-form
        :model="configForm"
        :rules="formRules"
        ref="configForm"
        label-width="120px">
        <el-form-item label="源状态" prop="fromStatus">
          <el-select v-model="configForm.fromStatus" placeholder="请选择源状态" style="width: 100%;">
            <el-option
              v-for="status in allStatuses"
              :key="status"
              :label="getStatusLabel(status)"
              :value="status">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="目标状态" prop="toStatus">
          <el-select v-model="configForm.toStatus" placeholder="请选择目标状态" style="width: 100%;">
            <el-option
              v-for="status in allStatuses"
              :key="status"
              :label="getStatusLabel(status)"
              :value="status">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="允许角色" prop="allowedRoles">
          <el-select
            v-model="configForm.allowedRolesList"
            multiple
            placeholder="请选择允许的角色"
            style="width: 100%;">
            <el-option
              v-for="role in availableRoles"
              :key="role.value"
              :label="role.label"
              :value="role.value">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="优先级" prop="priority">
          <el-input-number
            v-model="configForm.priority"
            :min="0"
            label="优先级">
          </el-input-number>
        </el-form-item>
        <el-form-item label="状态">
          <el-switch
            v-model="configForm.isActive"
            active-text="启用"
            inactive-text="禁用">
          </el-switch>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="configForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入配置描述">
          </el-input>
        </el-form-item>
        <el-form-item label="通知规则">
          <el-input
            v-model="configForm.notificationRules"
            type="textarea"
            :rows="2"
            placeholder="JSON格式的通知规则（可选）">
          </el-input>
        </el-form-item>
        <el-form-item label="自动流转条件">
          <el-input
            v-model="configForm.autoTransitionConditions"
            type="textarea"
            :rows="2"
            placeholder="JSON格式的自动流转条件（可选）">
          </el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveConfig" :loading="saving">
          {{ dialogMode === 'create' ? '创建' : '更新' }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 状态流转测试对话框 -->
    <el-dialog
      title="状态流转测试"
      :visible.sync="testDialogVisible"
      width="500px">
      <el-form label-width="120px">
        <el-form-item label="源状态">
          <el-tag :type="getStatusType(testForm.fromStatus)">
            {{ getStatusLabel(testForm.fromStatus) }}
          </el-tag>
        </el-form-item>
        <el-form-item label="目标状态">
          <el-tag :type="getStatusType(testForm.toStatus)">
            {{ getStatusLabel(testForm.toStatus) }}
          </el-tag>
        </el-form-item>
        <el-form-item label="当前用户角色">
          <el-tag>{{ getRoleLabel(currentUserRole) }}</el-tag>
        </el-form-item>
        <el-form-item label="测试结果">
          <el-button @click="performTransitionTest" type="primary" :loading="testing">
            执行测试
          </el-button>
        </el-form-item>
        <el-form-item v-if="testResult !== null">
          <el-alert
            :title="testResult.valid ? '流转有效' : '流转无效'"
            :type="testResult.valid ? 'success' : 'error'"
            show-icon
            :closable="false">
          </el-alert>
        </el-form-item>
      </el-form>
    </el-dialog>

    <!-- 规则验证结果对话框 -->
    <el-dialog
      title="规则验证结果"
      :visible.sync="validationDialogVisible"
      width="700px">
      <div v-if="validationResult">
        <el-alert
          :title="validationResult.valid ? '规则验证通过' : '规则验证发现问题'"
          :type="validationResult.valid ? 'success' : 'warning'"
          show-icon
          :closable="false"
          style="margin-bottom: 20px;">
        </el-alert>
        
        <el-descriptions :column="2" border>
          <el-descriptions-item label="总配置数">{{ validationResult.totalConfigs }}</el-descriptions-item>
          <el-descriptions-item label="总状态数">{{ validationResult.totalStatuses }}</el-descriptions-item>
          <el-descriptions-item label="错误数">{{ validationResult.errors.length }}</el-descriptions-item>
          <el-descriptions-item label="警告数">{{ validationResult.warnings.length }}</el-descriptions-item>
        </el-descriptions>

        <div v-if="validationResult.errors.length > 0" style="margin-top: 20px;">
          <h4>错误信息：</h4>
          <el-alert
            v-for="(error, index) in validationResult.errors"
            :key="index"
            :title="error"
            type="error"
            show-icon
            :closable="false"
            style="margin-bottom: 10px;">
          </el-alert>
        </div>

        <div v-if="validationResult.warnings.length > 0" style="margin-top: 20px;">
          <h4>警告信息：</h4>
          <el-alert
            v-for="(warning, index) in validationResult.warnings"
            :key="index"
            :title="warning"
            type="warning"
            show-icon
            :closable="false"
            style="margin-bottom: 10px;">
          </el-alert>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'StatusFlowConfigManagement',
  data() {
    return {
      loading: false,
      saving: false,
      testing: false,
      configs: [],
      allStatuses: [],
      selectedConfigs: [],
      searchParams: {
        fromStatus: '',
        toStatus: '',
        activeOnly: null
      },
      
      // 对话框相关
      dialogVisible: false,
      dialogMode: 'create', // 'create' | 'edit'
      configForm: {
        id: null,
        fromStatus: '',
        toStatus: '',
        allowedRolesList: [],
        priority: 0,
        isActive: true,
        description: '',
        notificationRules: '',
        autoTransitionConditions: ''
      },
      
      // 测试相关
      testDialogVisible: false,
      testForm: {
        fromStatus: '',
        toStatus: ''
      },
      testResult: null,
      
      // 验证相关
      validationDialogVisible: false,
      validationResult: null,
      
      // 当前用户信息
      currentUserRole: localStorage.getItem('userRole') || 'ENTERPRISE_USER',
      
      // 可用角色
      availableRoles: [
        { label: '超级管理员', value: 'SUPER_ADMIN' },
        { label: '管理员', value: 'ADMIN' },
        { label: '税务处理员', value: 'TAX_PROCESSOR' },
        { label: '企业用户', value: 'ENTERPRISE_USER' }
      ],
      
      // 表单验证规则
      formRules: {
        fromStatus: [
          { required: true, message: '请选择源状态', trigger: 'change' }
        ],
        toStatus: [
          { required: true, message: '请选择目标状态', trigger: 'change' }
        ],
        priority: [
          { required: true, message: '请输入优先级', trigger: 'blur' }
        ]
      }
    }
  },
  
  mounted() {
    this.loadAllStatuses()
    this.loadConfigs()
  },
  
  methods: {
    // 加载所有状态
    async loadAllStatuses() {
      try {
        const response = await this.$http.get('/enterprise-management/status-flow-config/all-statuses')
        if (response.data.success) {
          this.allStatuses = response.data.data
        }
      } catch (error) {
        this.$message.error('加载状态列表失败: ' + error.message)
      }
    },
    
    // 加载配置列表
    async loadConfigs() {
      this.loading = true
      try {
        const params = {}
        if (this.searchParams.fromStatus) params.fromStatus = this.searchParams.fromStatus
        if (this.searchParams.toStatus) params.toStatus = this.searchParams.toStatus
        if (this.searchParams.activeOnly !== null) params.activeOnly = this.searchParams.activeOnly
        
        const response = await this.$http.get('/enterprise-management/status-flow-config', { params })
        if (response.data.success) {
          this.configs = response.data.data
        }
      } catch (error) {
        this.$message.error('加载配置失败: ' + error.message)
      } finally {
        this.loading = false
      }
    },
    
    // 显示创建对话框
    showCreateDialog() {
      this.dialogMode = 'create'
      this.resetForm()
      this.dialogVisible = true
    },
    
    // 编辑配置
    editConfig(config) {
      this.dialogMode = 'edit'
      this.configForm = {
        id: config.id,
        fromStatus: config.fromStatus,
        toStatus: config.toStatus,
        allowedRolesList: this.parseRoles(config.allowedRoles),
        priority: config.priority,
        isActive: config.isActive,
        description: config.description,
        notificationRules: config.notificationRules,
        autoTransitionConditions: config.autoTransitionConditions
      }
      this.dialogVisible = true
    },
    
    // 保存配置
    saveConfig() {
      this.$refs.configForm.validate(async (valid) => {
        if (valid) {
          this.saving = true
          try {
            // 准备数据
            const requestData = {
              ...this.configForm,
              allowedRoles: JSON.stringify(this.configForm.allowedRolesList)
            }
            
            let response
            if (this.dialogMode === 'create') {
              response = await this.$http.post('/enterprise-management/status-flow-config', requestData)
            } else {
              response = await this.$http.put(`/enterprise-management/status-flow-config/${this.configForm.id}`, requestData)
            }
            
            if (response.data.success) {
              this.$message.success(this.dialogMode === 'create' ? '创建成功' : '更新成功')
              this.dialogVisible = false
              this.loadConfigs()
            }
          } catch (error) {
            this.$message.error((this.dialogMode === 'create' ? '创建失败: ' : '更新失败: ') + error.message)
          } finally {
            this.saving = false
          }
        }
      })
    },
    
    // 删除配置
    deleteConfig(config) {
      this.$confirm(`确定要删除 "${this.getStatusLabel(config.fromStatus)} → ${this.getStatusLabel(config.toStatus)}" 这项配置吗？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await this.$http.delete(`/enterprise-management/status-flow-config/${config.id}`)
          if (response.data.success) {
            this.$message.success('删除成功')
            this.loadConfigs()
          }
        } catch (error) {
          this.$message.error('删除失败: ' + error.message)
        }
      })
    },
    
    // 批量删除
    async batchDelete() {
      this.$confirm(`确定要删除选中的 ${this.selectedConfigs.length} 项配置吗？`, '确认批量删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const configIds = this.selectedConfigs.map(config => config.id)
          const response = await this.$http.delete('/enterprise-management/status-flow-config/batch', {
            data: { configIds }
          })
          if (response.data.success) {
            this.$message.success('批量删除成功')
            this.loadConfigs()
          }
        } catch (error) {
          this.$message.error('批量删除失败: ' + error.message)
        }
      })
    },
    
    // 切换启用状态
    async toggleActiveStatus(config) {
      try {
        const response = await this.$http.put(`/enterprise-management/status-flow-config/${config.id}/active-status`, {
          isActive: config.isActive
        })
        if (response.data.success) {
          this.$message.success(response.data.message)
        }
      } catch (error) {
        this.$message.error('更新状态失败: ' + error.message)
        // 恢复原状态
        config.isActive = !config.isActive
      }
    },
    
    // 测试状态流转
    testTransition(config) {
      this.testForm = {
        fromStatus: config.fromStatus,
        toStatus: config.toStatus
      }
      this.testResult = null
      this.testDialogVisible = true
    },
    
    // 执行流转测试
    async performTransitionTest() {
      this.testing = true
      try {
        const response = await this.$http.post('/enterprise-management/status-flow-config/validate-transition', {
          fromStatus: this.testForm.fromStatus,
          toStatus: this.testForm.toStatus
        })
        if (response.data.success) {
          this.testResult = response.data
        }
      } catch (error) {
        this.$message.error('测试失败: ' + error.message)
      } finally {
        this.testing = false
      }
    },
    
    // 初始化默认配置
    async initializeDefaults() {
      this.$confirm('确定要初始化默认配置吗？这将创建标准的状态流转规则。', '确认初始化', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(async () => {
        try {
          const response = await this.$http.post('/enterprise-management/status-flow-config/initialize-defaults')
          if (response.data.success) {
            this.$message.success('默认配置初始化成功')
            this.loadConfigs()
          }
        } catch (error) {
          this.$message.error('初始化失败: ' + error.message)
        }
      })
    },
    
    // 验证规则
    async validateRules() {
      try {
        const response = await this.$http.post('/enterprise-management/status-flow-config/validate-rules')
        if (response.data.success) {
          this.validationResult = response.data.data
          this.validationDialogVisible = true
        }
      } catch (error) {
        this.$message.error('验证失败: ' + error.message)
      }
    },
    
    // 执行自动流转
    async executeAutoTransition() {
      this.$confirm('确定要执行自动状态流转吗？', '确认执行', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await this.$http.post('/enterprise-management/status-flow-config/execute-auto-transition')
          if (response.data.success) {
            this.$message.success('自动流转执行完成')
          }
        } catch (error) {
          this.$message.error('执行失败: ' + error.message)
        }
      })
    },
    
    // 重置搜索
    resetSearch() {
      this.searchParams = {
        fromStatus: '',
        toStatus: '',
        activeOnly: null
      }
      this.loadConfigs()
    },
    
    // 重置表单
    resetForm() {
      this.configForm = {
        id: null,
        fromStatus: '',
        toStatus: '',
        allowedRolesList: [],
        priority: 0,
        isActive: true,
        description: '',
        notificationRules: '',
        autoTransitionConditions: ''
      }
      if (this.$refs.configForm) {
        this.$refs.configForm.resetFields()
      }
    },
    
    // 处理选择变更
    handleSelectionChange(selection) {
      this.selectedConfigs = selection
    },
    
    // 解析角色JSON
    parseRoles(rolesJson) {
      if (!rolesJson) return []
      try {
        return JSON.parse(rolesJson)
      } catch (e) {
        return []
      }
    },
    
    // 获取状态标签
    getStatusLabel(status) {
      const statusMap = {
        'PENDING': '待处理',
        'PROCESSING': '处理中',
        'COMPLETED': '已完成',
        'REJECTED': '已拒绝',
        'CANCELLED': '已取消'
      }
      return statusMap[status] || status
    },
    
    // 获取状态类型
    getStatusType(status) {
      const typeMap = {
        'PENDING': 'warning',
        'PROCESSING': 'primary',
        'COMPLETED': 'success',
        'REJECTED': 'danger',
        'CANCELLED': 'info'
      }
      return typeMap[status] || 'info'
    },
    
    // 获取角色标签
    getRoleLabel(role) {
      const roleMap = {
        'SUPER_ADMIN': '超级管理员',
        'ADMIN': '管理员',
        'TAX_PROCESSOR': '税务处理员',
        'ENTERPRISE_USER': '企业用户'
      }
      return roleMap[role] || role
    },
    
    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return ''
      return new Date(dateTime).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.status-flow-config-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e4e7ed;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.search-section {
  background: #f5f7fa;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.config-table {
  background: white;
  border-radius: 4px;
  overflow: hidden;
}

.batch-actions {
  margin-top: 20px;
  padding: 15px;
  background: #f5f7fa;
  border-radius: 4px;
}

.dialog-footer {
  text-align: right;
}

.el-tag {
  margin-right: 5px;
}

.el-descriptions {
  margin-bottom: 20px;
}
</style>